import React from 'react';
import { Box, Divider, Typography, useTheme } from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import { font } from '../../utils/fontHelper';
import ImageShimmer from '../widgets/ImageShimmer';
import InitialsAvatar from '../widgets/InitialsAvatar';
import PostActionBar from '../widgets/PostActionBar';

const MOVIES = [
  {
    title: 'Dune: Part Two',
    year: '2024',
    genre: 'Sci-Fi',
    rating: 4.5,
    poster: 'https://image.tmdb.org/t/p/w500/8hoD5BQuUV9dDecAbiyVIxLjzZ9.jpg',
  },
  {
    title: 'The Dark Knight',
    year: '2008',
    genre: 'Action',
    rating: 5,
    poster: 'https://image.tmdb.org/t/p/w500/r3UKPGdQC5mPiA9bE9otM9CN4lW.jpg',
  },
  {
    title: 'Pulp Fiction',
    year: '1994',
    genre: 'Crime',
    rating: 4,
    poster: 'https://image.tmdb.org/t/p/w500/vQWk5YBFWF4bZaofAbv0tShwBvQ.jpg',
  },
  {
    title: 'Amélie',
    year: '2001',
    genre: 'Romance',
    rating: 4.5,
    poster: 'https://image.tmdb.org/t/p/w500/nSxDa3M9aMvGVLoItzWTepQ5h5d.jpg',
  },
];

const MAIN = MOVIES[0];

// ── Mock post frame ──────────────────────────────────────────────────────────

const MockPostFrame: React.FC<{ label: string; description: string; children: React.ReactNode }> = ({
  label,
  description,
  children,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const dividerColor = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)';

  return (
    <Box sx={{ mb: 3 }}>
      <Typography sx={{ fontFamily: font(), fontWeight: 600, fontSize: 15, color: theme.palette.text.primary, mb: 0.25 }}>
        {label}
      </Typography>
      <Typography sx={{ fontFamily: font(), fontSize: 12, color: theme.palette.text.secondary, mb: 1.5 }}>
        {description}
      </Typography>

      {/* Post shell */}
      <Box sx={{ pb: 2.5, mb: 2, borderBottom: `1px solid ${dividerColor}` }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 2 }}>
          <Box sx={{ flexShrink: 0, mt: 0.5 }}>
            <InitialsAvatar name="Alok" size={34} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontFamily: font(), fontSize: 14, lineHeight: 1.4, color: theme.palette.text.primary }}>
              <strong>Alok</strong> wants to watch
            </Typography>
            <Typography sx={{ fontFamily: font(), fontSize: 12, color: theme.palette.text.secondary }}>
              2 hours ago
            </Typography>
          </Box>
        </Box>

        {/* Card variant */}
        {children}

        {/* Action bar */}
        <Box sx={{ mt: 1 }}>
          <PostActionBar
            likeCount={12}
            isLiked={false}
            commentCount={3}
            onLikeToggle={() => {}}
            onCommentClick={() => {}}
            disabled
          />
        </Box>
      </Box>
    </Box>
  );
};

// ── Variant A — Baseline ─────────────────────────────────────────────────────

const VariantA: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  return (
    <Box sx={{
      display: 'flex', alignItems: 'flex-start', gap: 1.5,
      p: 1.5, borderRadius: 2,
      bgcolor: isDark ? '#2C2C2E' : '#F0EFF0',
      minHeight: 100, overflow: 'hidden',
    }}>
      <Box sx={{ width: 70, height: 76, borderRadius: 1, overflow: 'hidden', flexShrink: 0, bgcolor: isDark ? '#3A3A3C' : '#E0E0E0' }}>
        <ImageShimmer src={MAIN.poster} alt={MAIN.title} width="100%" height="100%" objectFit="cover" borderRadius={1} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0, py: 0.5, display: 'flex', flexDirection: 'column' }}>
        <Typography sx={{ fontFamily: font(), fontWeight: 400, color: theme.palette.text.primary, fontSize: 15, mb: 0.25, lineHeight: 1.4, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
          {MAIN.title}
        </Typography>
        <Typography sx={{ fontFamily: font(), color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)', fontSize: 12, mb: 0.5 }}>
          {MAIN.year} ‧ {MAIN.genre}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <StarIcon sx={{ fontSize: 14, color: '#FF9500' }} />
          <Typography sx={{ fontFamily: font(), fontWeight: 500, fontSize: 12, color: isDark ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.9)' }}>
            {MAIN.rating}/5
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

// ── Variant B — Poster Backdrop Blur ─────────────────────────────────────────

const VariantB: React.FC = () => (
  <Box sx={{ position: 'relative', borderRadius: 2, overflow: 'hidden', minHeight: 100 }}>
    {/* blurred backdrop */}
    <Box sx={{
      position: 'absolute', inset: 0,
      backgroundImage: `url(${MAIN.poster})`,
      backgroundSize: 'cover', backgroundPosition: 'center',
      filter: 'blur(20px) brightness(0.3)',
      transform: 'scale(1.1)',
    }} />
    {/* content */}
    <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'flex-start', gap: 1.5, p: 1.5 }}>
      <Box sx={{ width: 70, height: 76, borderRadius: 1, overflow: 'hidden', flexShrink: 0 }}>
        <ImageShimmer src={MAIN.poster} alt={MAIN.title} width="100%" height="100%" objectFit="cover" borderRadius={1} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0, py: 0.5 }}>
        <Typography sx={{ fontFamily: font(), fontWeight: 500, color: '#fff', fontSize: 15, mb: 0.25, lineHeight: 1.4, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
          {MAIN.title}
        </Typography>
        <Typography sx={{ fontFamily: font(), color: 'rgba(255,255,255,0.6)', fontSize: 12, mb: 0.5 }}>
          {MAIN.year} ‧ {MAIN.genre}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <StarIcon sx={{ fontSize: 14, color: '#FFD700' }} />
          <Typography sx={{ fontFamily: font(), fontWeight: 500, fontSize: 12, color: 'rgba(255,255,255,0.9)' }}>
            {MAIN.rating}/5
          </Typography>
        </Box>
      </Box>
    </Box>
  </Box>
);

// ── Variant C — Glassmorphism ─────────────────────────────────────────────────

const VariantC: React.FC = () => (
  <Box sx={{ position: 'relative', borderRadius: 2, overflow: 'hidden', minHeight: 100 }}>
    {/* blurred backdrop for glass to sit on */}
    <Box sx={{
      position: 'absolute', inset: 0,
      backgroundImage: `url(${MAIN.poster})`,
      backgroundSize: 'cover', backgroundPosition: 'center',
      filter: 'blur(24px) brightness(0.25)',
      transform: 'scale(1.1)',
    }} />
    {/* glass card */}
    <Box sx={{
      position: 'relative', zIndex: 1,
      display: 'flex', alignItems: 'flex-start', gap: 1.5, p: 1.5,
      backdropFilter: 'blur(12px)',
      background: 'rgba(255,255,255,0.08)',
      border: '1px solid rgba(255,255,255,0.12)',
      borderRadius: 2,
    }}>
      <Box sx={{ width: 70, height: 76, borderRadius: 1, overflow: 'hidden', flexShrink: 0 }}>
        <ImageShimmer src={MAIN.poster} alt={MAIN.title} width="100%" height="100%" objectFit="cover" borderRadius={1} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0, py: 0.5 }}>
        <Typography sx={{ fontFamily: font(), fontWeight: 500, color: '#fff', fontSize: 15, mb: 0.25, lineHeight: 1.4, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
          {MAIN.title}
        </Typography>
        <Typography sx={{ fontFamily: font(), color: 'rgba(255,255,255,0.55)', fontSize: 12, mb: 0.5 }}>
          {MAIN.year} ‧ {MAIN.genre}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <StarIcon sx={{ fontSize: 14, color: '#FFD700' }} />
          <Typography sx={{ fontFamily: font(), fontWeight: 500, fontSize: 12, color: 'rgba(255,255,255,0.9)' }}>
            {MAIN.rating}/5
          </Typography>
        </Box>
      </Box>
    </Box>
  </Box>
);

// ── Variant D — Hero Poster ───────────────────────────────────────────────────

const VariantD: React.FC = () => (
  <Box sx={{ width: '100%', aspectRatio: '16/9', position: 'relative', overflow: 'hidden', borderRadius: 2 }}>
    <ImageShimmer src={MAIN.poster} alt={MAIN.title} width="100%" height="100%" objectFit="cover" borderRadius={2} />
    <Box sx={{
      position: 'absolute', bottom: 0, left: 0, right: 0,
      background: 'linear-gradient(to top, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.5) 55%, transparent 100%)',
      p: 1.5, pt: 3,
    }}>
      <Typography sx={{ fontFamily: font(), fontWeight: 600, color: '#fff', fontSize: 16, lineHeight: 1.3, mb: 0.25 }}>
        {MAIN.title}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography sx={{ fontFamily: font(), color: 'rgba(255,255,255,0.6)', fontSize: 12 }}>
          {MAIN.year} ‧ {MAIN.genre}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <StarIcon sx={{ fontSize: 13, color: '#FFD700' }} />
          <Typography sx={{ fontFamily: font(), fontWeight: 500, fontSize: 12, color: 'rgba(255,255,255,0.9)' }}>
            {MAIN.rating}/5
          </Typography>
        </Box>
      </Box>
    </Box>
  </Box>
);

// ── Variant E — Glowing Accent Border ────────────────────────────────────────

const VariantE: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  return (
    <Box sx={{
      display: 'flex', alignItems: 'flex-start', gap: 1.5,
      p: 1.5, borderRadius: 2,
      bgcolor: isDark ? '#2C2C2E' : '#F0EFF0',
      minHeight: 100, overflow: 'hidden',
      borderLeft: '3px solid #D30F45',
      boxShadow: '-2px 0 12px rgba(211,15,69,0.25)',
    }}>
      <Box sx={{ width: 70, height: 76, borderRadius: 1, overflow: 'hidden', flexShrink: 0, bgcolor: isDark ? '#3A3A3C' : '#E0E0E0' }}>
        <ImageShimmer src={MAIN.poster} alt={MAIN.title} width="100%" height="100%" objectFit="cover" borderRadius={1} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0, py: 0.5, display: 'flex', flexDirection: 'column' }}>
        <Typography sx={{ fontFamily: font(), fontWeight: 400, color: theme.palette.text.primary, fontSize: 15, mb: 0.25, lineHeight: 1.4, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
          {MAIN.title}
        </Typography>
        <Typography sx={{ fontFamily: font(), color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)', fontSize: 12, mb: 0.5 }}>
          {MAIN.year} ‧ {MAIN.genre}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <StarIcon sx={{ fontSize: 14, color: '#D30F45' }} />
          <Typography sx={{ fontFamily: font(), fontWeight: 500, fontSize: 12, color: isDark ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.9)' }}>
            {MAIN.rating}/5
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

// ── Variant F — Stacked Poster Fan ───────────────────────────────────────────

const VariantF: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const fanMovies = MOVIES.slice(0, 3);
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 2 }}>
      {/* Fan stack */}
      <Box sx={{ position: 'relative', width: 110, height: 150, flexShrink: 0 }}>
        {/* back */}
        <Box sx={{
          position: 'absolute', width: 80, height: 120, borderRadius: 1.5, overflow: 'hidden',
          transform: 'rotate(-8deg) translate(-10px, 4px)', opacity: 0.6,
          boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
        }}>
          <ImageShimmer src={fanMovies[2].poster} alt={fanMovies[2].title} width="100%" height="100%" objectFit="cover" borderRadius={1.5} />
        </Box>
        {/* middle */}
        <Box sx={{
          position: 'absolute', width: 80, height: 120, borderRadius: 1.5, overflow: 'hidden',
          transform: 'rotate(-3deg) translate(-4px, 1px)', opacity: 0.8,
          boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
        }}>
          <ImageShimmer src={fanMovies[1].poster} alt={fanMovies[1].title} width="100%" height="100%" objectFit="cover" borderRadius={1.5} />
        </Box>
        {/* front */}
        <Box sx={{
          position: 'absolute', width: 80, height: 120, borderRadius: 1.5, overflow: 'hidden',
          transform: 'rotate(2deg)',
          boxShadow: '0 6px 18px rgba(0,0,0,0.5)',
        }}>
          <ImageShimmer src={fanMovies[0].poster} alt={fanMovies[0].title} width="100%" height="100%" objectFit="cover" borderRadius={1.5} />
        </Box>
      </Box>

      {/* Info */}
      <Box sx={{ pb: 0.5 }}>
        <Typography sx={{ fontFamily: font(), fontWeight: 500, fontSize: 15, color: theme.palette.text.primary, mb: 0.5 }}>
          3 films
        </Typography>
        {fanMovies.map((m) => (
          <Typography key={m.title} sx={{ fontFamily: font(), fontSize: 12, color: isDark ? 'rgba(255,255,255,0.55)' : 'rgba(0,0,0,0.55)', lineHeight: 1.6 }}>
            {m.title}
          </Typography>
        ))}
      </Box>
    </Box>
  );
};

// ── Page ─────────────────────────────────────────────────────────────────────

const VARIANTS = [
  { label: 'A — Baseline (current)', description: 'Flat dark pill with small thumbnail — the existing design.', Component: VariantA },
  { label: 'B — Poster Backdrop Blur', description: "The movie's own poster is blurred and darkened to fill the card background.", Component: VariantB },
  { label: 'C — Glassmorphism', description: 'Glass card over a blurred poster backdrop — frosted, cinematic.', Component: VariantC },
  { label: 'D — Hero Poster', description: 'Full-width poster as the card itself, title pinned over a gradient scrim.', Component: VariantD },
  { label: 'E — Glowing Accent Border', description: 'Same layout as baseline but with a brand-red left border and subtle glow.', Component: VariantE },
  { label: 'F — Stacked Poster Fan', description: 'Three posters fanned like a hand of cards — for multi-movie posts.', Component: VariantF },
];

const CardPreviewPage: React.FC = () => {
  const theme = useTheme();

  return (
    <Box sx={{ maxWidth: 480, mx: 'auto', px: 2, py: 3, minHeight: '100vh', bgcolor: theme.palette.background.default }}>
      <Typography sx={{ fontFamily: font(), fontWeight: 700, fontSize: 20, color: theme.palette.text.primary, mb: 0.5 }}>
        Movie Card Variants
      </Typography>
      <Typography sx={{ fontFamily: font(), fontSize: 13, color: theme.palette.text.secondary, mb: 3 }}>
        Each shown as a crew "wants to watch" post. Pick a favourite.
      </Typography>

      {VARIANTS.map(({ label, description, Component }) => (
        <MockPostFrame key={label} label={label} description={description}>
          <Component />
        </MockPostFrame>
      ))}
    </Box>
  );
};

export default CardPreviewPage;
